// deps
import { useCallback, useContext, useState } from "react";

// internal-deps
import {
    CheckoutActionTypes,
    CheckoutContext,
    useNavigate,
    CheckoutRoute,
    DineoutCartContext,
    saveReferrerData,
    getReferrerDataFromQueryParams,
} from "@swiggy-private/checkout-core";
import { captureError } from "@swiggy-private/core-ui";

// hooks
import useRedirectToPayment from "./useRedirectToPayment";

// apis
import { validateDineoutCartWrapper } from "@dineout-cart-client/apis/Cart";

// constants
import { OOPS_SOMETHING_WENT_WRONG } from "@dineout-cart-client/constants";

// interfaces
import { IErrorMeta } from "@dineout-cart-client/interfaces";

interface IProps {
    skipValidation?: boolean;
    skipPayment?: boolean;
    cartType?: string;
    clickEvent?: () => void;
    setSoftError?: (softError: string) => void;
    setErrorPayload?: (errorPayload: IErrorMeta) => void;
    skipPaymentLink?: boolean;
}

interface IReturnType {
    isCartValidationInProgress: boolean;
    onClickProceed: VoidFunction;
    saveReferrerDataToSessionStorage: VoidFunction;
}

const useDineoutCartActions = (props: IProps): IReturnType => {
    const { skipValidation, cartType, skipPaymentLink, skipPayment, clickEvent, setSoftError, setErrorPayload } = props;
    // TODO: need to replace this with setSoft error or added in context
    const { redirect: redirectToPayment } = useRedirectToPayment({
        setSoftError,
        setErrorPayload,
        skipPaymentLink,
    });
    const [isCartValidationInProgress, setIsCartValidationInProgress] = useState(false);
    const { setCheckoutState } = useContext(CheckoutContext);
    const { cartState } = useContext(DineoutCartContext);
    const navigateTo = useNavigate();

    const validateCart = useCallback(async (cartType: string) => {
        const validationResponse = await validateDineoutCartWrapper(cartType);
        if (validationResponse.isErr()) {
            throw validationResponse;
        }
        return validationResponse.unwrap();
    }, []);

    const handleDirectCheckout = useCallback(() => {
        setCheckoutState({ type: CheckoutActionTypes.DINEOUT_DIRECT_PLACE_ORDER });
        navigateTo(CheckoutRoute.DINEOUT_DIRECT_PLACE_ORDER, {}, false);
    }, [navigateTo, setCheckoutState]);

    const closeValidationPopup = useCallback(() => {
        setIsCartValidationInProgress(false);
    }, []);

    const saveReferrerDataToSessionStorage = useCallback(() => {
        let referrer = cartState.localCart?.cartMetaData?.referrer;
        let referrerId: string | number | undefined = cartState.localCart?.cartMetaData?.referrerId;

        // Fallback to query params if not present in cart metadata
        try {
            if (!referrer || !referrerId) {
                const queryParams = getReferrerDataFromQueryParams();
                referrer = queryParams.referrer ?? "";
                referrerId = queryParams.referrerId ?? "";
            }
        } catch (error) {
            captureError(error);
        }

        saveReferrerData(referrer, referrerId ?? "");
    }, [cartState]);

    const onClickProceed = useCallback(() => {
        clickEvent?.();

        // Save referrer data to session storage for both payment and skip payment flows
        saveReferrerDataToSessionStorage();

        if (skipValidation || !cartType) {
            skipPayment ? handleDirectCheckout() : redirectToPayment();
            return;
        }

        setIsCartValidationInProgress(true);
        validateCart(cartType)
            .then(({ isValidCart }) => {
                if (!isValidCart) {
                    throw new Error(OOPS_SOMETHING_WENT_WRONG);
                }
                setIsCartValidationInProgress(false);
                skipPayment ? handleDirectCheckout() : redirectToPayment(closeValidationPopup);
            })
            .catch((e?: { error?: { statusMessage: string } }) => {
                setSoftError?.(e?.error?.statusMessage || OOPS_SOMETHING_WENT_WRONG);
                setIsCartValidationInProgress(false);
            });
    }, [
        clickEvent,
        skipValidation,
        cartType,
        validateCart,
        skipPayment,
        handleDirectCheckout,
        redirectToPayment,
        setSoftError,
        closeValidationPopup,
        saveReferrerDataToSessionStorage,
    ]);

    return { onClickProceed, isCartValidationInProgress, saveReferrerDataToSessionStorage };
};

export default useDineoutCartActions;
